import React, { useState } from "react";
import { exportUserActivity } from "../../api/UserActivityApi";
import { getUsersList } from "../../api/ProfileApi";
import { toastrOnTopCenter } from "../../utils/toastr";
import AsyncSelect from "react-select/async";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import DotPulseBackdrop from "../shared/components/DotPulseBackdrop";
import AuthenticatedLayout from "../../layouts/AuthenticatedLayout";

const UserActivityExport = () => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const loadUsers = async (inputValue) => {
    if (!inputValue || inputValue.length < 2) {
      return [];
    }

    try {
      const response = await getUsersList({
        query: inputValue,
        page: 1,
      });
      
      const users = response.data?.collection || [];
      
      return users.map((user) => ({
        label: `${user.name || "Unknown"} (${user.email || "No email"})`,
        value: user.email,
        name: user.name,
        email: user.email,
      }));
    } catch (error) {
      console.error("Error searching users:", error);
      return [];
    }
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
    // If end date is before new start date, clear it
    if (endDate && date && date > endDate) {
      setEndDate(null);
    }
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    // If start date is after new end date, clear it
    if (startDate && date && date < startDate) {
      setStartDate(null);
    }
  };

  const formatDateForAPI = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleDownload = async () => {
    if (!selectedUser) {
      toastrOnTopCenter("Please select a user first", "warning");
      return;
    }

    if (startDate && endDate && startDate > endDate) {
      toastrOnTopCenter("Start date must be before or equal to end date", "warning");
      return;
    }

    setIsDownloading(true);
    try {
      const formattedStartDate = formatDateForAPI(startDate);
      const formattedEndDate = formatDateForAPI(endDate);
      const response = await exportUserActivity(
        selectedUser.email,
        formattedStartDate,
        formattedEndDate
      );
      
      // Create blob from response
      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers["content-disposition"];
      let filename = `user_activity_${selectedUser.email}_${new Date().toISOString().split("T")[0]}.csv`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toastrOnTopCenter("User activity exported successfully", "success");
    } catch (error) {
      console.error("Error exporting user activity:", error);
      const errorMessage = error?.message || "Failed to export user activity";
      toastrOnTopCenter(errorMessage, "error");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header pb-0">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h6 className="mb-0">User Activity Export</h6>
                  <p className="text-sm text-muted mb-0">
                    Search for a user by name or email and download their activity report as CSV
                  </p>
                </div>
              </div>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-8">
                  <div className="form-group">
                    <label htmlFor="user-search" className="form-control-label">
                      Search User (Name or Email)
                    </label>
                    <AsyncSelect
                      id="user-search"
                      cacheOptions
                      defaultOptions={false}
                      loadOptions={loadUsers}
                      value={selectedUser}
                      onChange={(user) => setSelectedUser(user)}
                      placeholder="Type to search users (minimum 2 characters)..."
                      noOptionsMessage={({ inputValue }) =>
                        inputValue.length < 2
                          ? "Type at least 2 characters to search"
                          : "No users found"
                      }
                      isClearable
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          minHeight: "38px",
                        }),
                      }}
                    />
                  </div>
                </div>
                <div className="col-md-4 d-flex align-items-end">
                  <button
                    className="btn btn-primary w-100"
                    onClick={handleDownload}
                    disabled={!selectedUser || isDownloading}
                    style={{ minHeight: "38px" }}
                  >
                    {isDownloading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Downloading...
                      </>
                    ) : (
                      <>
                        <i className="fa fa-download me-2"></i>
                        Download CSV
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="start-date" className="form-control-label">
                      Start Date (Optional)
                    </label>
                    <DatePicker
                      id="start-date"
                      selected={startDate}
                      onChange={handleStartDateChange}
                      dateFormat="MM/dd/yyyy"
                      placeholderText="Select start date"
                      maxDate={endDate || new Date()}
                      isClearable
                      className="form-control"
                      customInput={<input className="form-control" />}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="end-date" className="form-control-label">
                      End Date (Optional)
                    </label>
                    <DatePicker
                      id="end-date"
                      selected={endDate}
                      onChange={handleEndDateChange}
                      dateFormat="MM/dd/yyyy"
                      placeholderText="Select end date"
                      minDate={startDate}
                      maxDate={new Date()}
                      isClearable
                      className="form-control"
                      customInput={<input className="form-control" />}
                    />
                  </div>
                </div>
              </div>

              {selectedUser && (
                <div className="row mt-4">
                  <div className="col-12">
                    <div className="alert alert-info mb-0">
                      <strong>Selected User:</strong> {selectedUser.name} ({selectedUser.email})
                      <br />
                      {startDate && endDate && (
                        <>
                          <strong>Date Range:</strong> {startDate.toLocaleDateString()} to {endDate.toLocaleDateString()}
                          <br />
                        </>
                      )}
                      {startDate && !endDate && (
                        <>
                          <strong>Start Date:</strong> {startDate.toLocaleDateString()}
                          <br />
                        </>
                      )}
                      {!startDate && endDate && (
                        <>
                          <strong>End Date:</strong> {endDate.toLocaleDateString()}
                          <br />
                        </>
                      )}
                      <small>
                        {startDate || endDate
                          ? "Click 'Download CSV' to export activity data for the selected date range."
                          : "Click 'Download CSV' to export all activity data for this user."}
                      </small>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {isDownloading && <DotPulseBackdrop />}
    </div>
  );
};

export default AuthenticatedLayout(UserActivityExport);

